/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ArchiveDataSourcePathParameters,
  CopyDataSourcePathParameters,
  DeleteDataSourcePathParameters,
  GetArchivedDataSourcesParams,
  GetDataSourcePathParameters,
  GetDataSourcePreviewParams,
  GetDataSourcePreviewPathParameters,
  GetDataSourceSchemaParams,
  GetDataSourceSchemaPathParameters,
  GetDataSourcesParams,
  RestoreDataSourcePathParameters,
  TLBulkActionResponseFoundryTableAttachResult,
  TLBulkActionResponseWorkflowOutputAttachResult,
  TLBulkAttachFoundryTablesRequest,
  TLBulkAttachWorkflowOutputsRequest,
  TLCopyDataSourceRequest,
  TLCopyDataSourceResponse,
  TLDataSourceContentUpdateResponse,
  TLDataSourceCreateRequest,
  TLDataSourcePreviewResponse,
  TLDataSourceResponse,
  TLDataSourceSchemaResponse,
  TLDataSourcesResponse,
  TLDataSourceUpdateRequest,
  TLHTTPValidationError,
  TLSuccessResponse,
  TLUpdateDirectSourceContentDataHarmonizationRequest,
  UpdateDataSourcePathParameters,
  UpdateDirectSourceContentPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Data Sources
 */
export const getDataSources = (
  params?: GetDataSourcesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataSourcesResponse>(
    { url: `/v2/data-harmonization/data-sources`, method: 'GET', params, signal },
    options,
  );
};

export const getGetDataSourcesQueryKey = (params?: GetDataSourcesParams) => {
  return ['v2', 'data-harmonization', 'data-sources', ...(params ? [params] : [])] as const;
};

export const getGetDataSourcesQueryOptions = <
  TData = Awaited<ReturnType<typeof getDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetDataSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDataSourcesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDataSources>>> = ({ signal }) =>
    getDataSources(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getDataSources>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDataSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof getDataSources>>>;
export type GetDataSourcesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDataSources<
  TData = Awaited<ReturnType<typeof getDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetDataSourcesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSources>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataSources>>,
          TError,
          Awaited<ReturnType<typeof getDataSources>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDataSources<
  TData = Awaited<ReturnType<typeof getDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetDataSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSources>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataSources>>,
          TError,
          Awaited<ReturnType<typeof getDataSources>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDataSources<
  TData = Awaited<ReturnType<typeof getDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetDataSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Data Sources
 */

export function useGetDataSources<
  TData = Awaited<ReturnType<typeof getDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetDataSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDataSourcesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Data Sources
 */
export const prefetchGetDataSourcesQuery = async <
  TData = Awaited<ReturnType<typeof getDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetDataSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDataSourcesQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Data Sources
 */
export const invalidateGetDataSources = async (
  queryClient: QueryClient,
  params?: GetDataSourcesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetDataSourcesQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Data Source
 */
export const createDataSource = (
  tLDataSourceCreateRequest: BodyType<TLDataSourceCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataSourceResponse>(
    {
      url: `/v2/data-harmonization/data-sources`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataSourceCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateDataSourceQueryKey = (tLDataSourceCreateRequest?: BodyType<TLDataSourceCreateRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'data-sources', tLDataSourceCreateRequest] as const;
};

export const getCreateDataSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof createDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataSourceCreateRequest: BodyType<TLDataSourceCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateDataSourceQueryKey(tLDataSourceCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createDataSource>>> = ({ signal }) =>
    createDataSource(tLDataSourceCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createDataSource>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateDataSourceQueryResult = NonNullable<Awaited<ReturnType<typeof createDataSource>>>;
export type CreateDataSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateDataSource<
  TData = Awaited<ReturnType<typeof createDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataSourceCreateRequest: BodyType<TLDataSourceCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDataSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createDataSource>>,
          TError,
          Awaited<ReturnType<typeof createDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateDataSource<
  TData = Awaited<ReturnType<typeof createDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataSourceCreateRequest: BodyType<TLDataSourceCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDataSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createDataSource>>,
          TError,
          Awaited<ReturnType<typeof createDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateDataSource<
  TData = Awaited<ReturnType<typeof createDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataSourceCreateRequest: BodyType<TLDataSourceCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Data Source
 */

export function useCreateDataSource<
  TData = Awaited<ReturnType<typeof createDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataSourceCreateRequest: BodyType<TLDataSourceCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateDataSourceQueryOptions(tLDataSourceCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Data Source
 */
export const prefetchCreateDataSourceQuery = async <
  TData = Awaited<ReturnType<typeof createDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLDataSourceCreateRequest: BodyType<TLDataSourceCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateDataSourceQueryOptions(tLDataSourceCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Data Source
 */
export const invalidateCreateDataSource = async (
  queryClient: QueryClient,
  tLDataSourceCreateRequest: BodyType<TLDataSourceCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateDataSourceQueryKey(tLDataSourceCreateRequest) }, options);

  return queryClient;
};

/**
 * @summary Get Archived Data Sources
 */
export const getArchivedDataSources = (
  params: GetArchivedDataSourcesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataSourcesResponse>(
    { url: `/v2/data-harmonization/data-sources/archived`, method: 'GET', params, signal },
    options,
  );
};

export const getGetArchivedDataSourcesQueryKey = (params?: GetArchivedDataSourcesParams) => {
  return ['v2', 'data-harmonization', 'data-sources', 'archived', ...(params ? [params] : [])] as const;
};

export const getGetArchivedDataSourcesQueryOptions = <
  TData = Awaited<ReturnType<typeof getArchivedDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetArchivedDataSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getArchivedDataSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetArchivedDataSourcesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getArchivedDataSources>>> = ({ signal }) =>
    getArchivedDataSources(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getArchivedDataSources>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetArchivedDataSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof getArchivedDataSources>>>;
export type GetArchivedDataSourcesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetArchivedDataSources<
  TData = Awaited<ReturnType<typeof getArchivedDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetArchivedDataSourcesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getArchivedDataSources>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getArchivedDataSources>>,
          TError,
          Awaited<ReturnType<typeof getArchivedDataSources>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArchivedDataSources<
  TData = Awaited<ReturnType<typeof getArchivedDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetArchivedDataSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getArchivedDataSources>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getArchivedDataSources>>,
          TError,
          Awaited<ReturnType<typeof getArchivedDataSources>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArchivedDataSources<
  TData = Awaited<ReturnType<typeof getArchivedDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetArchivedDataSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getArchivedDataSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Archived Data Sources
 */

export function useGetArchivedDataSources<
  TData = Awaited<ReturnType<typeof getArchivedDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetArchivedDataSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getArchivedDataSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetArchivedDataSourcesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Archived Data Sources
 */
export const prefetchGetArchivedDataSourcesQuery = async <
  TData = Awaited<ReturnType<typeof getArchivedDataSources>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: GetArchivedDataSourcesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getArchivedDataSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetArchivedDataSourcesQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Archived Data Sources
 */
export const invalidateGetArchivedDataSources = async (
  queryClient: QueryClient,
  params: GetArchivedDataSourcesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetArchivedDataSourcesQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Bulk Attach Foundry Tables
 */
export const bulkAttachFoundryTables = (
  tLBulkAttachFoundryTablesRequest: BodyType<TLBulkAttachFoundryTablesRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBulkActionResponseFoundryTableAttachResult>(
    {
      url: `/v2/data-harmonization/data-sources/bulk-attach-foundry-tables`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLBulkAttachFoundryTablesRequest,
      signal,
    },
    options,
  );
};

export const getBulkAttachFoundryTablesQueryKey = (
  tLBulkAttachFoundryTablesRequest?: BodyType<TLBulkAttachFoundryTablesRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'data-sources',
    'bulk-attach-foundry-tables',
    tLBulkAttachFoundryTablesRequest,
  ] as const;
};

export const getBulkAttachFoundryTablesQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkAttachFoundryTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkAttachFoundryTablesRequest: BodyType<TLBulkAttachFoundryTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachFoundryTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkAttachFoundryTablesQueryKey(tLBulkAttachFoundryTablesRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkAttachFoundryTables>>> = ({ signal }) =>
    bulkAttachFoundryTables(tLBulkAttachFoundryTablesRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkAttachFoundryTables>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkAttachFoundryTablesQueryResult = NonNullable<Awaited<ReturnType<typeof bulkAttachFoundryTables>>>;
export type BulkAttachFoundryTablesQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkAttachFoundryTables<
  TData = Awaited<ReturnType<typeof bulkAttachFoundryTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkAttachFoundryTablesRequest: BodyType<TLBulkAttachFoundryTablesRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachFoundryTables>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkAttachFoundryTables>>,
          TError,
          Awaited<ReturnType<typeof bulkAttachFoundryTables>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkAttachFoundryTables<
  TData = Awaited<ReturnType<typeof bulkAttachFoundryTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkAttachFoundryTablesRequest: BodyType<TLBulkAttachFoundryTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachFoundryTables>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkAttachFoundryTables>>,
          TError,
          Awaited<ReturnType<typeof bulkAttachFoundryTables>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkAttachFoundryTables<
  TData = Awaited<ReturnType<typeof bulkAttachFoundryTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkAttachFoundryTablesRequest: BodyType<TLBulkAttachFoundryTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachFoundryTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Attach Foundry Tables
 */

export function useBulkAttachFoundryTables<
  TData = Awaited<ReturnType<typeof bulkAttachFoundryTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkAttachFoundryTablesRequest: BodyType<TLBulkAttachFoundryTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachFoundryTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkAttachFoundryTablesQueryOptions(tLBulkAttachFoundryTablesRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Attach Foundry Tables
 */
export const prefetchBulkAttachFoundryTablesQuery = async <
  TData = Awaited<ReturnType<typeof bulkAttachFoundryTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLBulkAttachFoundryTablesRequest: BodyType<TLBulkAttachFoundryTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachFoundryTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkAttachFoundryTablesQueryOptions(tLBulkAttachFoundryTablesRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Attach Foundry Tables
 */
export const invalidateBulkAttachFoundryTables = async (
  queryClient: QueryClient,
  tLBulkAttachFoundryTablesRequest: BodyType<TLBulkAttachFoundryTablesRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkAttachFoundryTablesQueryKey(tLBulkAttachFoundryTablesRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Bulk Attach Workflow Outputs
 */
export const bulkAttachWorkflowOutputs = (
  tLBulkAttachWorkflowOutputsRequest: BodyType<TLBulkAttachWorkflowOutputsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBulkActionResponseWorkflowOutputAttachResult>(
    {
      url: `/v2/data-harmonization/data-sources/bulk-attach-workflow-outputs`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLBulkAttachWorkflowOutputsRequest,
      signal,
    },
    options,
  );
};

export const getBulkAttachWorkflowOutputsQueryKey = (
  tLBulkAttachWorkflowOutputsRequest?: BodyType<TLBulkAttachWorkflowOutputsRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'data-sources',
    'bulk-attach-workflow-outputs',
    tLBulkAttachWorkflowOutputsRequest,
  ] as const;
};

export const getBulkAttachWorkflowOutputsQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkAttachWorkflowOutputsRequest: BodyType<TLBulkAttachWorkflowOutputsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkAttachWorkflowOutputsQueryKey(tLBulkAttachWorkflowOutputsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>> = ({ signal }) =>
    bulkAttachWorkflowOutputs(tLBulkAttachWorkflowOutputsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkAttachWorkflowOutputsQueryResult = NonNullable<Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>>;
export type BulkAttachWorkflowOutputsQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkAttachWorkflowOutputs<
  TData = Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkAttachWorkflowOutputsRequest: BodyType<TLBulkAttachWorkflowOutputsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>,
          TError,
          Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkAttachWorkflowOutputs<
  TData = Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkAttachWorkflowOutputsRequest: BodyType<TLBulkAttachWorkflowOutputsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>,
          TError,
          Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkAttachWorkflowOutputs<
  TData = Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkAttachWorkflowOutputsRequest: BodyType<TLBulkAttachWorkflowOutputsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Attach Workflow Outputs
 */

export function useBulkAttachWorkflowOutputs<
  TData = Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkAttachWorkflowOutputsRequest: BodyType<TLBulkAttachWorkflowOutputsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkAttachWorkflowOutputsQueryOptions(tLBulkAttachWorkflowOutputsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Attach Workflow Outputs
 */
export const prefetchBulkAttachWorkflowOutputsQuery = async <
  TData = Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLBulkAttachWorkflowOutputsRequest: BodyType<TLBulkAttachWorkflowOutputsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAttachWorkflowOutputs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkAttachWorkflowOutputsQueryOptions(tLBulkAttachWorkflowOutputsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Attach Workflow Outputs
 */
export const invalidateBulkAttachWorkflowOutputs = async (
  queryClient: QueryClient,
  tLBulkAttachWorkflowOutputsRequest: BodyType<TLBulkAttachWorkflowOutputsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkAttachWorkflowOutputsQueryKey(tLBulkAttachWorkflowOutputsRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Archive Data Source
 */
export const archiveDataSource = (
  { sourceId }: ArchiveDataSourcePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/data-sources/${sourceId}`, method: 'DELETE', signal },
    options,
  );
};

export const getArchiveDataSourceQueryKey = ({ sourceId }: ArchiveDataSourcePathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'data-sources', sourceId] as const;
};

export const getArchiveDataSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof archiveDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: ArchiveDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getArchiveDataSourceQueryKey({ sourceId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof archiveDataSource>>> = ({ signal }) =>
    archiveDataSource({ sourceId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: sourceId !== null && sourceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof archiveDataSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ArchiveDataSourceQueryResult = NonNullable<Awaited<ReturnType<typeof archiveDataSource>>>;
export type ArchiveDataSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useArchiveDataSource<
  TData = Awaited<ReturnType<typeof archiveDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ArchiveDataSourcePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveDataSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof archiveDataSource>>,
          TError,
          Awaited<ReturnType<typeof archiveDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useArchiveDataSource<
  TData = Awaited<ReturnType<typeof archiveDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ArchiveDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveDataSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof archiveDataSource>>,
          TError,
          Awaited<ReturnType<typeof archiveDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useArchiveDataSource<
  TData = Awaited<ReturnType<typeof archiveDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ArchiveDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Archive Data Source
 */

export function useArchiveDataSource<
  TData = Awaited<ReturnType<typeof archiveDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: ArchiveDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getArchiveDataSourceQueryOptions({ sourceId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Archive Data Source
 */
export const prefetchArchiveDataSourceQuery = async <
  TData = Awaited<ReturnType<typeof archiveDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceId }: ArchiveDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getArchiveDataSourceQueryOptions({ sourceId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Archive Data Source
 */
export const invalidateArchiveDataSource = async (
  queryClient: QueryClient,
  { sourceId }: ArchiveDataSourcePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getArchiveDataSourceQueryKey({ sourceId }) }, options);

  return queryClient;
};

/**
 * @summary Get Data Source
 */
export const getDataSource = (
  { sourceId }: GetDataSourcePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataSourceResponse>(
    { url: `/v2/data-harmonization/data-sources/${sourceId}`, method: 'GET', signal },
    options,
  );
};

export const getGetDataSourceQueryKey = ({ sourceId }: GetDataSourcePathParameters) => {
  return ['v2', 'data-harmonization', 'data-sources', sourceId] as const;
};

export const getGetDataSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof getDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: GetDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDataSourceQueryKey({ sourceId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDataSource>>> = ({ signal }) =>
    getDataSource({ sourceId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: sourceId !== null && sourceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDataSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDataSourceQueryResult = NonNullable<Awaited<ReturnType<typeof getDataSource>>>;
export type GetDataSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDataSource<
  TData = Awaited<ReturnType<typeof getDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDataSourcePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataSource>>,
          TError,
          Awaited<ReturnType<typeof getDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDataSource<
  TData = Awaited<ReturnType<typeof getDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataSource>>,
          TError,
          Awaited<ReturnType<typeof getDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDataSource<
  TData = Awaited<ReturnType<typeof getDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Data Source
 */

export function useGetDataSource<
  TData = Awaited<ReturnType<typeof getDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: GetDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDataSourceQueryOptions({ sourceId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Data Source
 */
export const prefetchGetDataSourceQuery = async <
  TData = Awaited<ReturnType<typeof getDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceId }: GetDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDataSourceQueryOptions({ sourceId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Data Source
 */
export const invalidateGetDataSource = async (
  queryClient: QueryClient,
  { sourceId }: GetDataSourcePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetDataSourceQueryKey({ sourceId }) }, options);

  return queryClient;
};

/**
 * @summary Update Data Source
 */
export const updateDataSource = (
  { sourceId }: UpdateDataSourcePathParameters,
  tLDataSourceUpdateRequest: BodyType<TLDataSourceUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataSourceResponse>(
    {
      url: `/v2/data-harmonization/data-sources/${sourceId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataSourceUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateDataSourceQueryKey = (
  { sourceId }: UpdateDataSourcePathParameters,
  tLDataSourceUpdateRequest?: BodyType<TLDataSourceUpdateRequest>,
) => {
  return ['PATCH', 'v2', 'data-harmonization', 'data-sources', sourceId, tLDataSourceUpdateRequest] as const;
};

export const getUpdateDataSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof updateDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: UpdateDataSourcePathParameters,
  tLDataSourceUpdateRequest: BodyType<TLDataSourceUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateDataSourceQueryKey({ sourceId }, tLDataSourceUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateDataSource>>> = ({ signal }) =>
    updateDataSource({ sourceId }, tLDataSourceUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: sourceId !== null && sourceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateDataSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateDataSourceQueryResult = NonNullable<Awaited<ReturnType<typeof updateDataSource>>>;
export type UpdateDataSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateDataSource<
  TData = Awaited<ReturnType<typeof updateDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDataSourcePathParameters,
  tLDataSourceUpdateRequest: BodyType<TLDataSourceUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDataSource>>,
          TError,
          Awaited<ReturnType<typeof updateDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDataSource<
  TData = Awaited<ReturnType<typeof updateDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDataSourcePathParameters,
  tLDataSourceUpdateRequest: BodyType<TLDataSourceUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDataSource>>,
          TError,
          Awaited<ReturnType<typeof updateDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDataSource<
  TData = Awaited<ReturnType<typeof updateDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDataSourcePathParameters,
  tLDataSourceUpdateRequest: BodyType<TLDataSourceUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Data Source
 */

export function useUpdateDataSource<
  TData = Awaited<ReturnType<typeof updateDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: UpdateDataSourcePathParameters,
  tLDataSourceUpdateRequest: BodyType<TLDataSourceUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateDataSourceQueryOptions({ sourceId }, tLDataSourceUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Data Source
 */
export const prefetchUpdateDataSourceQuery = async <
  TData = Awaited<ReturnType<typeof updateDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceId }: UpdateDataSourcePathParameters,
  tLDataSourceUpdateRequest: BodyType<TLDataSourceUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateDataSourceQueryOptions({ sourceId }, tLDataSourceUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Data Source
 */
export const invalidateUpdateDataSource = async (
  queryClient: QueryClient,
  { sourceId }: UpdateDataSourcePathParameters,
  tLDataSourceUpdateRequest: BodyType<TLDataSourceUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateDataSourceQueryKey({ sourceId }, tLDataSourceUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Direct Source Content
 */
export const updateDirectSourceContent = (
  { sourceId }: UpdateDirectSourceContentPathParameters,
  tLUpdateDirectSourceContentDataHarmonizationRequest: BodyType<TLUpdateDirectSourceContentDataHarmonizationRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  formData.append(`file`, tLUpdateDirectSourceContentDataHarmonizationRequest.file);

  return apiInstance<TLDataSourceContentUpdateResponse>(
    {
      url: `/v2/data-harmonization/data-sources/${sourceId}/content`,
      method: 'PUT',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      signal,
    },
    options,
  );
};

export const getUpdateDirectSourceContentQueryKey = (
  { sourceId }: UpdateDirectSourceContentPathParameters,
  tLUpdateDirectSourceContentDataHarmonizationRequest?: BodyType<TLUpdateDirectSourceContentDataHarmonizationRequest>,
) => {
  return [
    'PUT',
    'v2',
    'data-harmonization',
    'data-sources',
    sourceId,
    'content',
    tLUpdateDirectSourceContentDataHarmonizationRequest,
  ] as const;
};

export const getUpdateDirectSourceContentQueryOptions = <
  TData = Awaited<ReturnType<typeof updateDirectSourceContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: UpdateDirectSourceContentPathParameters,
  tLUpdateDirectSourceContentDataHarmonizationRequest: BodyType<TLUpdateDirectSourceContentDataHarmonizationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDirectSourceContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUpdateDirectSourceContentQueryKey({ sourceId }, tLUpdateDirectSourceContentDataHarmonizationRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateDirectSourceContent>>> = ({ signal }) =>
    updateDirectSourceContent(
      { sourceId },
      tLUpdateDirectSourceContentDataHarmonizationRequest,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: sourceId !== null && sourceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateDirectSourceContent>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateDirectSourceContentQueryResult = NonNullable<Awaited<ReturnType<typeof updateDirectSourceContent>>>;
export type UpdateDirectSourceContentQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateDirectSourceContent<
  TData = Awaited<ReturnType<typeof updateDirectSourceContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDirectSourceContentPathParameters,
  tLUpdateDirectSourceContentDataHarmonizationRequest: BodyType<TLUpdateDirectSourceContentDataHarmonizationRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDirectSourceContent>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDirectSourceContent>>,
          TError,
          Awaited<ReturnType<typeof updateDirectSourceContent>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDirectSourceContent<
  TData = Awaited<ReturnType<typeof updateDirectSourceContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDirectSourceContentPathParameters,
  tLUpdateDirectSourceContentDataHarmonizationRequest: BodyType<TLUpdateDirectSourceContentDataHarmonizationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDirectSourceContent>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDirectSourceContent>>,
          TError,
          Awaited<ReturnType<typeof updateDirectSourceContent>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDirectSourceContent<
  TData = Awaited<ReturnType<typeof updateDirectSourceContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDirectSourceContentPathParameters,
  tLUpdateDirectSourceContentDataHarmonizationRequest: BodyType<TLUpdateDirectSourceContentDataHarmonizationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDirectSourceContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Direct Source Content
 */

export function useUpdateDirectSourceContent<
  TData = Awaited<ReturnType<typeof updateDirectSourceContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: UpdateDirectSourceContentPathParameters,
  tLUpdateDirectSourceContentDataHarmonizationRequest: BodyType<TLUpdateDirectSourceContentDataHarmonizationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDirectSourceContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateDirectSourceContentQueryOptions(
    { sourceId },
    tLUpdateDirectSourceContentDataHarmonizationRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Direct Source Content
 */
export const prefetchUpdateDirectSourceContentQuery = async <
  TData = Awaited<ReturnType<typeof updateDirectSourceContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceId }: UpdateDirectSourceContentPathParameters,
  tLUpdateDirectSourceContentDataHarmonizationRequest: BodyType<TLUpdateDirectSourceContentDataHarmonizationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDirectSourceContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateDirectSourceContentQueryOptions(
    { sourceId },
    tLUpdateDirectSourceContentDataHarmonizationRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Direct Source Content
 */
export const invalidateUpdateDirectSourceContent = async (
  queryClient: QueryClient,
  { sourceId }: UpdateDirectSourceContentPathParameters,
  tLUpdateDirectSourceContentDataHarmonizationRequest: BodyType<TLUpdateDirectSourceContentDataHarmonizationRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    {
      queryKey: getUpdateDirectSourceContentQueryKey({ sourceId }, tLUpdateDirectSourceContentDataHarmonizationRequest),
    },
    options,
  );

  return queryClient;
};

/**
 * @summary Copy Data Source
 */
export const copyDataSource = (
  { sourceId }: CopyDataSourcePathParameters,
  tLCopyDataSourceRequest: BodyType<TLCopyDataSourceRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCopyDataSourceResponse>(
    {
      url: `/v2/data-harmonization/data-sources/${sourceId}/copy`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCopyDataSourceRequest,
      signal,
    },
    options,
  );
};

export const getCopyDataSourceQueryKey = (
  { sourceId }: CopyDataSourcePathParameters,
  tLCopyDataSourceRequest?: BodyType<TLCopyDataSourceRequest>,
) => {
  return ['POST', 'v2', 'data-harmonization', 'data-sources', sourceId, 'copy', tLCopyDataSourceRequest] as const;
};

export const getCopyDataSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof copyDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: CopyDataSourcePathParameters,
  tLCopyDataSourceRequest: BodyType<TLCopyDataSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof copyDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCopyDataSourceQueryKey({ sourceId }, tLCopyDataSourceRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof copyDataSource>>> = ({ signal }) =>
    copyDataSource({ sourceId }, tLCopyDataSourceRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: sourceId !== null && sourceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof copyDataSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CopyDataSourceQueryResult = NonNullable<Awaited<ReturnType<typeof copyDataSource>>>;
export type CopyDataSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useCopyDataSource<
  TData = Awaited<ReturnType<typeof copyDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CopyDataSourcePathParameters,
  tLCopyDataSourceRequest: BodyType<TLCopyDataSourceRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof copyDataSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof copyDataSource>>,
          TError,
          Awaited<ReturnType<typeof copyDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCopyDataSource<
  TData = Awaited<ReturnType<typeof copyDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CopyDataSourcePathParameters,
  tLCopyDataSourceRequest: BodyType<TLCopyDataSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof copyDataSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof copyDataSource>>,
          TError,
          Awaited<ReturnType<typeof copyDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCopyDataSource<
  TData = Awaited<ReturnType<typeof copyDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CopyDataSourcePathParameters,
  tLCopyDataSourceRequest: BodyType<TLCopyDataSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof copyDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Copy Data Source
 */

export function useCopyDataSource<
  TData = Awaited<ReturnType<typeof copyDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: CopyDataSourcePathParameters,
  tLCopyDataSourceRequest: BodyType<TLCopyDataSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof copyDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCopyDataSourceQueryOptions({ sourceId }, tLCopyDataSourceRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Copy Data Source
 */
export const prefetchCopyDataSourceQuery = async <
  TData = Awaited<ReturnType<typeof copyDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceId }: CopyDataSourcePathParameters,
  tLCopyDataSourceRequest: BodyType<TLCopyDataSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof copyDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCopyDataSourceQueryOptions({ sourceId }, tLCopyDataSourceRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Copy Data Source
 */
export const invalidateCopyDataSource = async (
  queryClient: QueryClient,
  { sourceId }: CopyDataSourcePathParameters,
  tLCopyDataSourceRequest: BodyType<TLCopyDataSourceRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCopyDataSourceQueryKey({ sourceId }, tLCopyDataSourceRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Data Source
 */
export const deleteDataSource = (
  { sourceId }: DeleteDataSourcePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/data-sources/${sourceId}/delete`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteDataSourceQueryKey = ({ sourceId }: DeleteDataSourcePathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'data-sources', sourceId, 'delete'] as const;
};

export const getDeleteDataSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: DeleteDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteDataSourceQueryKey({ sourceId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteDataSource>>> = ({ signal }) =>
    deleteDataSource({ sourceId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: sourceId !== null && sourceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteDataSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteDataSourceQueryResult = NonNullable<Awaited<ReturnType<typeof deleteDataSource>>>;
export type DeleteDataSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteDataSource<
  TData = Awaited<ReturnType<typeof deleteDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDataSourcePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDataSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteDataSource>>,
          TError,
          Awaited<ReturnType<typeof deleteDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteDataSource<
  TData = Awaited<ReturnType<typeof deleteDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDataSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteDataSource>>,
          TError,
          Awaited<ReturnType<typeof deleteDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteDataSource<
  TData = Awaited<ReturnType<typeof deleteDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Data Source
 */

export function useDeleteDataSource<
  TData = Awaited<ReturnType<typeof deleteDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: DeleteDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteDataSourceQueryOptions({ sourceId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Data Source
 */
export const prefetchDeleteDataSourceQuery = async <
  TData = Awaited<ReturnType<typeof deleteDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceId }: DeleteDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteDataSourceQueryOptions({ sourceId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Data Source
 */
export const invalidateDeleteDataSource = async (
  queryClient: QueryClient,
  { sourceId }: DeleteDataSourcePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteDataSourceQueryKey({ sourceId }) }, options);

  return queryClient;
};

/**
 * @summary Get Data Source Preview
 */
export const getDataSourcePreview = (
  { sourceId }: GetDataSourcePreviewPathParameters,
  params?: GetDataSourcePreviewParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataSourcePreviewResponse>(
    { url: `/v2/data-harmonization/data-sources/${sourceId}/preview`, method: 'GET', params, signal },
    options,
  );
};

export const getGetDataSourcePreviewQueryKey = (
  { sourceId }: GetDataSourcePreviewPathParameters,
  params?: GetDataSourcePreviewParams,
) => {
  return ['v2', 'data-harmonization', 'data-sources', sourceId, 'preview', ...(params ? [params] : [])] as const;
};

export const getGetDataSourcePreviewQueryOptions = <
  TData = Awaited<ReturnType<typeof getDataSourcePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: GetDataSourcePreviewPathParameters,
  params?: GetDataSourcePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourcePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDataSourcePreviewQueryKey({ sourceId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDataSourcePreview>>> = ({ signal }) =>
    getDataSourcePreview({ sourceId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: sourceId !== null && sourceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDataSourcePreview>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDataSourcePreviewQueryResult = NonNullable<Awaited<ReturnType<typeof getDataSourcePreview>>>;
export type GetDataSourcePreviewQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDataSourcePreview<
  TData = Awaited<ReturnType<typeof getDataSourcePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDataSourcePreviewPathParameters,
  params: undefined | GetDataSourcePreviewParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourcePreview>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataSourcePreview>>,
          TError,
          Awaited<ReturnType<typeof getDataSourcePreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDataSourcePreview<
  TData = Awaited<ReturnType<typeof getDataSourcePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDataSourcePreviewPathParameters,
  params?: GetDataSourcePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourcePreview>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataSourcePreview>>,
          TError,
          Awaited<ReturnType<typeof getDataSourcePreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDataSourcePreview<
  TData = Awaited<ReturnType<typeof getDataSourcePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDataSourcePreviewPathParameters,
  params?: GetDataSourcePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourcePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Data Source Preview
 */

export function useGetDataSourcePreview<
  TData = Awaited<ReturnType<typeof getDataSourcePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: GetDataSourcePreviewPathParameters,
  params?: GetDataSourcePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourcePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDataSourcePreviewQueryOptions({ sourceId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Data Source Preview
 */
export const prefetchGetDataSourcePreviewQuery = async <
  TData = Awaited<ReturnType<typeof getDataSourcePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceId }: GetDataSourcePreviewPathParameters,
  params?: GetDataSourcePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourcePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDataSourcePreviewQueryOptions({ sourceId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Data Source Preview
 */
export const invalidateGetDataSourcePreview = async (
  queryClient: QueryClient,
  { sourceId }: GetDataSourcePreviewPathParameters,
  params?: GetDataSourcePreviewParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetDataSourcePreviewQueryKey({ sourceId }, params) }, options);

  return queryClient;
};

/**
 * @summary Restore Data Source
 */
export const restoreDataSource = (
  { sourceId }: RestoreDataSourcePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataSourceResponse>(
    { url: `/v2/data-harmonization/data-sources/${sourceId}/restore`, method: 'POST', signal },
    options,
  );
};

export const getRestoreDataSourceQueryKey = ({ sourceId }: RestoreDataSourcePathParameters) => {
  return ['POST', 'v2', 'data-harmonization', 'data-sources', sourceId, 'restore'] as const;
};

export const getRestoreDataSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof restoreDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: RestoreDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRestoreDataSourceQueryKey({ sourceId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof restoreDataSource>>> = ({ signal }) =>
    restoreDataSource({ sourceId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: sourceId !== null && sourceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof restoreDataSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RestoreDataSourceQueryResult = NonNullable<Awaited<ReturnType<typeof restoreDataSource>>>;
export type RestoreDataSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useRestoreDataSource<
  TData = Awaited<ReturnType<typeof restoreDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RestoreDataSourcePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreDataSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof restoreDataSource>>,
          TError,
          Awaited<ReturnType<typeof restoreDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRestoreDataSource<
  TData = Awaited<ReturnType<typeof restoreDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RestoreDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreDataSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof restoreDataSource>>,
          TError,
          Awaited<ReturnType<typeof restoreDataSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRestoreDataSource<
  TData = Awaited<ReturnType<typeof restoreDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RestoreDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Restore Data Source
 */

export function useRestoreDataSource<
  TData = Awaited<ReturnType<typeof restoreDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: RestoreDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRestoreDataSourceQueryOptions({ sourceId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Restore Data Source
 */
export const prefetchRestoreDataSourceQuery = async <
  TData = Awaited<ReturnType<typeof restoreDataSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceId }: RestoreDataSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof restoreDataSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRestoreDataSourceQueryOptions({ sourceId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Restore Data Source
 */
export const invalidateRestoreDataSource = async (
  queryClient: QueryClient,
  { sourceId }: RestoreDataSourcePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRestoreDataSourceQueryKey({ sourceId }) }, options);

  return queryClient;
};

/**
 * @summary Get Data Source Schema
 */
export const getDataSourceSchema = (
  { sourceId }: GetDataSourceSchemaPathParameters,
  params?: GetDataSourceSchemaParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataSourceSchemaResponse>(
    { url: `/v2/data-harmonization/data-sources/${sourceId}/schema`, method: 'GET', params, signal },
    options,
  );
};

export const getGetDataSourceSchemaQueryKey = (
  { sourceId }: GetDataSourceSchemaPathParameters,
  params?: GetDataSourceSchemaParams,
) => {
  return ['v2', 'data-harmonization', 'data-sources', sourceId, 'schema', ...(params ? [params] : [])] as const;
};

export const getGetDataSourceSchemaQueryOptions = <
  TData = Awaited<ReturnType<typeof getDataSourceSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: GetDataSourceSchemaPathParameters,
  params?: GetDataSourceSchemaParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourceSchema>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDataSourceSchemaQueryKey({ sourceId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDataSourceSchema>>> = ({ signal }) =>
    getDataSourceSchema({ sourceId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: sourceId !== null && sourceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getDataSourceSchema>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetDataSourceSchemaQueryResult = NonNullable<Awaited<ReturnType<typeof getDataSourceSchema>>>;
export type GetDataSourceSchemaQueryError = ErrorType<TLHTTPValidationError>;

export function useGetDataSourceSchema<
  TData = Awaited<ReturnType<typeof getDataSourceSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDataSourceSchemaPathParameters,
  params: undefined | GetDataSourceSchemaParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourceSchema>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataSourceSchema>>,
          TError,
          Awaited<ReturnType<typeof getDataSourceSchema>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDataSourceSchema<
  TData = Awaited<ReturnType<typeof getDataSourceSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDataSourceSchemaPathParameters,
  params?: GetDataSourceSchemaParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourceSchema>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataSourceSchema>>,
          TError,
          Awaited<ReturnType<typeof getDataSourceSchema>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDataSourceSchema<
  TData = Awaited<ReturnType<typeof getDataSourceSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetDataSourceSchemaPathParameters,
  params?: GetDataSourceSchemaParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourceSchema>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Data Source Schema
 */

export function useGetDataSourceSchema<
  TData = Awaited<ReturnType<typeof getDataSourceSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceId }: GetDataSourceSchemaPathParameters,
  params?: GetDataSourceSchemaParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourceSchema>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetDataSourceSchemaQueryOptions({ sourceId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Data Source Schema
 */
export const prefetchGetDataSourceSchemaQuery = async <
  TData = Awaited<ReturnType<typeof getDataSourceSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceId }: GetDataSourceSchemaPathParameters,
  params?: GetDataSourceSchemaParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getDataSourceSchema>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetDataSourceSchemaQueryOptions({ sourceId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Data Source Schema
 */
export const invalidateGetDataSourceSchema = async (
  queryClient: QueryClient,
  { sourceId }: GetDataSourceSchemaPathParameters,
  params?: GetDataSourceSchemaParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetDataSourceSchemaQueryKey({ sourceId }, params) }, options);

  return queryClient;
};
